import * as api from "./client.js";
import type { TaxonomyMode } from "./submissions.service.js";

export type ActivityLogFilters = {
  action?: string;
  resourceType?: string;
  resourceId?: string;
  actorId?: string;
  search?: string;
  limit?: number;
  offset?: number;
};

// --- Existing types ---

export type BrowseFolderItem = {
  type: "folder";
  name: string;
  serverRelativeUrl: string;
  itemCount: number;
  timeLastModified: string;
};

export type BrowseFileItem = {
  type: "file";
  name: string;
  sharepointFileId: string;
  fileUrl: string;
  size: number;
  timeCreated: string;
  timeLastModified: string;
  parseStatus: string | null;
  parseResultId: string | null;
  submissionEntryFileId: string | null;
};

export type BrowseItem = BrowseFolderItem | BrowseFileItem;

export type TimelineEvent = {
  step: string;
  status: "success" | "error" | "skipped";
  timestamp: string;
  details?: string;
  durationMs?: number;
};

// One reason a list row deserves a human's attention, as the API reports it (see
// DocParseAttentionReasonSchema in submission-api). "any" is the filter-only union.
export type DocParseAttentionReason = "verdict" | "empty" | "no_template" | "failed";
export type DocParseAttention = DocParseAttentionReason | "any";

export type DocParseResult = {
  id: string;
  sharepointFileId: string | null;
  fileName: string;
  originalFileName: string | null;
  /** Submitting firm, resolved list-side only — get-by-id does not carry it. */
  firmName: string | null;
  submissionEntryFileId: string | null;
  status: "waiting" | "queued" | "processing" | "parsed" | "failed";
  parsedJson: unknown | null;
  errorMessage: string | null;
  createdAt: string;
  updatedAt: string;
  parsedAt: string | null;
  timeline: TimelineEvent[] | null;
  retryCount: number;
  /** How loudly the row is marked for a human to look at. Null when the parser flagged nothing. */
  barReviewTier: "major" | "minor" | null;
  barReviewReasons: Record<string, number> | null;
  /**
   * Why the row deserves a look, in the API's fixed order (verdict, empty, no_template, failed).
   * List-side only — get-by-id does not carry it — hence optional on the shared shape.
   */
  attentionReasons?: DocParseAttentionReason[];
};

export type DocParseFilterOptions = {
  editions: string[];
  publications: {
    id: number | null;
    name: string;
    editionId: string | null;
    edition: string | null;
  }[];
  regions: { id: number | null; name: string; rankingEntityId: string | null }[];
  practiceAreas: { id: number | null; name: string; bpaId: string | null }[];
};

type FilterOptionsResponse = { options: DocParseFilterOptions };

export type ParsingJobsFilters = {
  status?: string;
  search?: string;
  limit?: number;
  offset?: number;
  edition?: string;
  publicationId?: number;
  regionId?: number;
  practiceAreaId?: number;
  editionId?: string;
  rankingEntityId?: string;
  bpaId?: string;
  taxonomy?: TaxonomyMode;
  emptyHead?: boolean;
  emptyClients?: boolean;
  emptyContacts?: boolean;
  emptyMatters?: boolean;
  barReview?: boolean;
  /** "uk_bar_2027", "bar_family_unknown", or "none" for results with no Bar template recorded. */
  barTemplate?: string;
  /** One of DocParseAttention: a single reason, or "any" for their union. Supersedes barReview. */
  attention?: string;
  olderThanMinutes?: number;
};

export type ParsingJobsListResponse = {
  results: DocParseResult[];
  pagination: { total: number; limit: number; offset: number };
};

export type ActivityLogEntry = {
  id: string;
  actorId: string | null;
  actorType: string | null;
  actorRole: string | null;
  actorFirmRef: string | null;
  actorIsImpersonating: boolean | null;
  action: string;
  resourceType: string;
  resourceId: string;
  details: Record<string, unknown> | null;
  createdAt: string;
};

export type ActivityLogListResponse = {
  results: ActivityLogEntry[];
  pagination: { total: number; limit: number; offset: number };
  requestId: string;
};

export type SubmissionContext = {
  originalFileName: string;
  firmRef: string | null;
  publicationName: string;
  practiceAreaName: string | null;
  practiceGroupName: string | null;
  regionName: string | null;
  edition: string | null;
  tableName: string | null;
  subtype: string | null;
  submissionType: string;
  refereeType: string;
  firmName: string;
  publicationId: number | null;
  editorialCategoryId: number | null;
  practiceAreaId: number | null;
  practiceGroupId: number | null;
  regionId: number | null;
  tableId: number | null;
  fullUri: string | null;
};

export type QueueStats = {
  name: string;
  counts: {
    waiting: number;
    active: number;
    completed: number;
    failed: number;
    delayed: number;
    paused: number;
  };
  lastExecutedAt: number | null;
  recentFailedJobs: Array<{
    id: string;
    name: string;
    failedReason: string | null;
    attemptsMade: number;
    timestamp: number | null;
    finishedOn: number | null;
    data: unknown;
  }>;
  activeJobs: Array<{
    id: string;
    name: string;
    timestamp: number | null;
    processedOn: number | null;
    data: unknown;
  }>;
};

export type QueueStatsResponse = { queue: QueueStats };

// Mirrors BarParseStatsSchema in submission-api. ukBar2027 buckets are exclusive and sum to total;
// barFamilyUnknown and failedWithoutParsedJson are outside that base (see the card's footnote).
export type BarParseStats = {
  ukBar2027: {
    total: number;
    clean: number;
    minor: number;
    major: number;
    failed: number;
    inProgress: number;
  };
  barFamilyUnknown: number;
  failedWithoutParsedJson: number;
};

export type BarParseStatsResponse = { stats: BarParseStats };

type SubmissionContextResponse = { context: SubmissionContext | null };

export type ParseUnparsedFilters = {
  edition?: string;
  publicationId?: number;
  regionId?: number;
  practiceAreaId?: number;
  editionId?: string;
  rankingEntityId?: string;
  bpaId?: string;
  taxonomy?: TaxonomyMode;
  search?: string;
};

export type ParseUnparsedResponse = {
  enqueuedCount: number;
  skippedCount: number;
  requestId?: string;
};

export type UnparsedListFile = {
  submissionEntryFileId: string;
  fileName: string;
};

export type UnparsedListResponse = {
  files: UnparsedListFile[];
  total: number;
  requestId?: string;
};

type BrowseResponse = { path: string; items: BrowseItem[] };
type ParseResponse = { result: DocParseResult };
type ResultResponse = { result: DocParseResult };

export type FullParseResultStatus =
  | "parsed"
  | "schema_validated"
  | "failed"
  | "waiting"
  | "queued"
  | "processing";

export type FullParseSubmissionContext = {
  edition: string | null;
  publicationId: number | null;
  publicationName: string | null;
  regionId: number | null;
  regionName: string | null;
  practiceAreaId: number | null;
  practiceAreaName: string | null;
  editorialCategoryId: number | null;
  tableId: number | null;
  tableName: string | null;
  firmRef: string | null;
  firmName: string | null;
  originalFileName: string | null;
  fileUri: string | null;
};

/** `GET /v1/submission/doc-parse-results/:id/full`: one parse result with everything the parser stored. */
export type FullParseResultResponse = {
  id: string;
  status: FullParseResultStatus;
  errorMessage: string | null;
  parsedAt: string | null;
  templateId: string | null;
  hasDocument: boolean;
  parsedJson: unknown;
  submissionContext: FullParseSubmissionContext | null;
};

export type FullParseResultEnvelope = {
  result: FullParseResultResponse;
  requestId: string;
};

export const docParsing = {
  getQueueStats(): Promise<QueueStatsResponse> {
    return api.get<QueueStatsResponse>("/v1/submission/doc-parsing/queue-stats");
  },

  getBarStats(): Promise<BarParseStatsResponse> {
    return api.get<BarParseStatsResponse>("/v1/submission/doc-parsing/bar-stats");
  },

  listActivityLogs(filters?: ActivityLogFilters): Promise<ActivityLogListResponse> {
    const params = new URLSearchParams();
    if (filters?.action) params.append("action", filters.action);
    if (filters?.resourceType) params.append("resourceType", filters.resourceType);
    if (filters?.resourceId) params.append("resourceId", filters.resourceId);
    if (filters?.actorId) params.append("actorId", filters.actorId);
    if (filters?.search) params.append("search", filters.search);
    if (filters?.limit !== undefined) params.append("limit", String(filters.limit));
    if (filters?.offset !== undefined) params.append("offset", String(filters.offset));
    const query = params.toString();
    return api.get<ActivityLogListResponse>(
      `/v1/submission/activity-logs${query ? `?${query}` : ""}`,
    );
  },

  listResults(filters?: ParsingJobsFilters): Promise<ParsingJobsListResponse> {
    const params = new URLSearchParams();
    if (filters?.status) params.append("status", filters.status);
    if (filters?.search) params.append("search", filters.search);
    if (filters?.limit !== undefined) params.append("limit", String(filters.limit));
    if (filters?.offset !== undefined) params.append("offset", String(filters.offset));
    if (filters?.edition) params.append("edition", filters.edition);
    if (filters?.publicationId !== undefined)
      params.append("publicationId", String(filters.publicationId));
    if (filters?.regionId !== undefined) params.append("regionId", String(filters.regionId));
    if (filters?.practiceAreaId !== undefined)
      params.append("practiceAreaId", String(filters.practiceAreaId));
    if (filters?.editionId) params.append("editionId", filters.editionId);
    if (filters?.rankingEntityId) params.append("rankingEntityId", filters.rankingEntityId);
    if (filters?.bpaId) params.append("bpaId", filters.bpaId);
    if (filters?.emptyHead) params.append("emptyHead", "true");
    if (filters?.emptyClients) params.append("emptyClients", "true");
    if (filters?.emptyContacts) params.append("emptyContacts", "true");
    if (filters?.emptyMatters) params.append("emptyMatters", "true");
    if (filters?.barReview) params.append("barReview", "true");
    if (filters?.barTemplate) params.append("barTemplate", filters.barTemplate);
    if (filters?.attention) params.append("attention", filters.attention);
    if (filters?.olderThanMinutes !== undefined)
      params.append("olderThanMinutes", String(filters.olderThanMinutes));
    if (filters?.taxonomy) params.append("taxonomy", filters.taxonomy);
    const query = params.toString();
    return api.get<ParsingJobsListResponse>(
      `/v1/submission/doc-parsing/results${query ? `?${query}` : ""}`,
    );
  },

  getFilterOptions(taxonomy?: TaxonomyMode): Promise<FilterOptionsResponse> {
    const query = taxonomy ? `?taxonomy=${taxonomy}` : "";
    return api.get<FilterOptionsResponse>(`/v1/submission/doc-parsing/filter-options${query}`);
  },

  retryAll(
    filters: Omit<ParsingJobsFilters, "limit" | "offset">,
  ): Promise<{ enqueuedCount: number; skippedCount: number }> {
    const params = new URLSearchParams();
    if (filters.status) params.append("status", filters.status);
    if (filters.search) params.append("search", filters.search);
    if (filters.edition) params.append("edition", filters.edition);
    if (filters.publicationId !== undefined)
      params.append("publicationId", String(filters.publicationId));
    if (filters.regionId !== undefined) params.append("regionId", String(filters.regionId));
    if (filters.practiceAreaId !== undefined)
      params.append("practiceAreaId", String(filters.practiceAreaId));
    if (filters.editionId) params.append("editionId", filters.editionId);
    if (filters.rankingEntityId) params.append("rankingEntityId", filters.rankingEntityId);
    if (filters.bpaId) params.append("bpaId", filters.bpaId);
    if (filters.emptyHead) params.append("emptyHead", "true");
    if (filters.emptyClients) params.append("emptyClients", "true");
    if (filters.emptyContacts) params.append("emptyContacts", "true");
    if (filters.emptyMatters) params.append("emptyMatters", "true");
    if (filters.barReview) params.append("barReview", "true");
    if (filters.barTemplate) params.append("barTemplate", filters.barTemplate);
    if (filters.attention) params.append("attention", filters.attention);
    if (filters.olderThanMinutes !== undefined)
      params.append("olderThanMinutes", String(filters.olderThanMinutes));
    if (filters.taxonomy) params.append("taxonomy", filters.taxonomy);
    const query = params.toString();
    return api.post(`/v1/submission/doc-parsing/retry-all${query ? `?${query}` : ""}`, {});
  },

  browse(path?: string): Promise<BrowseResponse> {
    const query = path ? `?path=${encodeURIComponent(path)}` : "";
    return api.get<BrowseResponse>(`/v1/submission/doc-parsing/sharepoint/browse${query}`);
  },

  parseFile(data: {
    sharepointFileId: string;
    fileName: string;
    submissionEntryFileId?: string;
  }): Promise<ParseResponse> {
    return api.post<ParseResponse>(`/v1/submission/doc-parsing/parse`, data);
  },

  parseEntryFile(submissionEntryFileId: string): Promise<void> {
    return api.post<void>(`/v1/submission/doc-parsing/parse-entry-file`, {
      submissionEntryFileId,
    });
  },

  listUnparsed(filters: ParseUnparsedFilters): Promise<UnparsedListResponse> {
    const params = new URLSearchParams();
    if (filters.edition) params.append("edition", filters.edition);
    if (filters.publicationId !== undefined)
      params.append("publicationId", String(filters.publicationId));
    if (filters.regionId !== undefined) params.append("regionId", String(filters.regionId));
    if (filters.practiceAreaId !== undefined)
      params.append("practiceAreaId", String(filters.practiceAreaId));
    if (filters.editionId) params.append("editionId", filters.editionId);
    if (filters.rankingEntityId) params.append("rankingEntityId", filters.rankingEntityId);
    if (filters.bpaId) params.append("bpaId", filters.bpaId);
    if (filters.search) params.append("search", filters.search);
    if (filters.taxonomy) params.append("taxonomy", filters.taxonomy);
    const query = params.toString();
    return api.get<UnparsedListResponse>(
      `/v1/submission/doc-parsing/unparsed${query ? `?${query}` : ""}`,
    );
  },

  parseEntryFiles(submissionEntryFileIds: string[]): Promise<ParseUnparsedResponse> {
    return api.post<ParseUnparsedResponse>(`/v1/submission/doc-parsing/parse-entry-files`, {
      submissionEntryFileIds,
    });
  },

  getResultById(id: string): Promise<ResultResponse> {
    return api.get<ResultResponse>(`/v1/submission/doc-parsing/results/${id}`);
  },

  getFullResult(id: string): Promise<FullParseResultEnvelope> {
    return api.get<FullParseResultEnvelope>(`/v1/submission/doc-parse-results/${id}/full`);
  },

  async downloadFile(sharepointFileId: string, fileName: string): Promise<void> {
    const query = `?fileName=${encodeURIComponent(fileName)}`;
    const response = await api.fetchRaw(
      `/v1/submission/doc-parsing/sharepoint/download/${encodeURIComponent(sharepointFileId)}${query}`,
    );
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(
        (errorData as { error?: string }).error || `Download failed: HTTP ${response.status}`,
      );
    }
    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = fileName;
    a.click();
    setTimeout(() => window.URL.revokeObjectURL(url), 1000);
  },

  /** Saves the document a parse result was parsed from; the server picks the file by the result's id. */
  async downloadParseResultDocument(docParseResultId: string, fileName: string): Promise<void> {
    const response = await api.fetchRaw(
      `/v1/submission/doc-parse-results/${encodeURIComponent(docParseResultId)}/document`,
    );
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(
        (errorData as { error?: string }).error || `Download failed: HTTP ${response.status}`,
      );
    }
    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = fileName;
    a.click();
    setTimeout(() => window.URL.revokeObjectURL(url), 1000);
  },

  async replaceFile(
    sharepointFileId: string,
    file: File,
    submissionEntryFileId: string,
  ): Promise<{ message: string; fileName: string; parseStatus: string }> {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("submissionEntryFileId", submissionEntryFileId);
    const response = await api.fetchRaw(
      `/v1/submission/doc-parsing/sharepoint/replace/${encodeURIComponent(sharepointFileId)}`,
      {
        method: "PUT",
        body: formData,
      },
    );
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(
        (errorData as { error?: string }).error || `Replace failed: HTTP ${response.status}`,
      );
    }
    return response.json();
  },

  getSubmissionContext(id: string): Promise<SubmissionContextResponse> {
    return api.get<SubmissionContextResponse>(
      `/v1/submission/doc-parsing/results/${id}/submission-context`,
    );
  },

  getFileUrl(id: string): Promise<{ fullUri: string | null; requestId?: string }> {
    return api.get<{ fullUri: string | null; requestId?: string }>(
      `/v1/submission/doc-parsing/results/${id}/file-url`,
    );
  },
};
